'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useState, useEffect } from 'react';

export default function Navigation() {
  const t = useTranslations('Home.Navigation');
  const [activeSection, setActiveSection] = useState('Home');

  // Check URL hash on mount and when hash changes
  useEffect(() => {
    const checkHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        setActiveSection(hash);
      }
    };

    checkHash();
    window.addEventListener('hashchange', checkHash);

    return () => {
      window.removeEventListener('hashchange', checkHash);
    };
  }, []);

  // Scroll detection for sections - with improved responsiveness
  useEffect(() => {
    const sections = [
      'Home',
      'ClientsSection',
      'WorkSection',
      'ServicesSection',
      'WorkStatsSection',
      'CEOWordSection',
      'TrendsSection',
      'ContactUsSection',
    ];

    let timeoutId: number;
    let lastScrollTop = 0;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const scrollDirection = scrollTop > lastScrollTop ? 'down' : 'up';
      lastScrollTop = scrollTop;

      // Clear previous timeout
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      // Set new timeout to check section after scrolling stops
      timeoutId = window.setTimeout(() => {
        const scrollPosition = scrollTop + 100;

        // Find which section is currently in view
        let currentSection = 'Home';
        for (const sectionId of sections) {
          const element = document.getElementById(sectionId);
          if (element) {
            const { offsetTop, offsetHeight } = element;
            if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
              currentSection = sectionId;
              break;
            }
          }
        }

        // Only update if section actually changed
        if (currentSection !== activeSection) {
          setActiveSection(currentSection);

          // Update URL hash without triggering hashchange event
          const newUrl = `${window.location.pathname}#${currentSection}`;
          window.history.replaceState(null, '', newUrl);

          console.log('📍 Navigation updated to:', currentSection, 'Direction:', scrollDirection);
        }
      }, 100); // Reduced to 100ms for better responsiveness
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [activeSection]);

  const handleNavClick = (sectionId: string) => {
    setActiveSection(sectionId);

    // Scroll to section
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getNavItemClass = (sectionId: string) => {
    const baseClass =
      "cursor-pointer px-3.5 py-2.5 no-underline rtl:before:ml-2 ltr:before:mr-2 before:opacity-0 before:content-['●'] hover:rounded-4xl hover:text-white hover:before:opacity-100";

    if (sectionId === activeSection) {
      return `${baseClass} bg-primary-400 rounded-4xl text-white before:opacity-100`;
    }

    return `${baseClass} text-[#5A5A59] hover:bg-primary-400`;
  };

  return (
    <nav className="">
      <ul className={`flex flex-col items-center gap-10 lg:flex-row lg:gap-1`}>
        <li className="">
          <Link
            href="#Home"
            className={getNavItemClass('Home')}
            onClick={() => handleNavClick('Home')}>
            {t('Home')}
          </Link>
        </li>
        <li>
          <Link
            href="#ClientsSection"
            className={getNavItemClass('ClientsSection')}
            onClick={() => handleNavClick('ClientsSection')}>
            {t('OurClients')}
          </Link>
        </li>
        <li>
          <Link
            href="#WorkSection"
            className={getNavItemClass('WorkSection')}
            onClick={() => handleNavClick('WorkSection')}>
            {t('OurWork')}
          </Link>
        </li>
        <li>
          <Link
            href="#ServicesSection"
            className={getNavItemClass('ServicesSection')}
            onClick={() => handleNavClick('ServicesSection')}>
            {t('Services')}
          </Link>
        </li>
        <li>
          <Link
            href="#WorkStatsSection"
            className={getNavItemClass('WorkStatsSection')}
            onClick={() => handleNavClick('WorkStatsSection')}>
            {t('WhyUs')}
          </Link>
        </li>
        <li>
          <Link
            href="#ContactUsSection"
            className={getNavItemClass('ContactUsSection')}
            onClick={() => handleNavClick('ContactUsSection')}>
            {t('ContactUs')}
          </Link>
        </li>
      </ul>
    </nav>
  );
}
